import { createWindowVirtualizer } from "@tanstack/solid-virtual";
import { For, createMemo, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import type { GridColumns } from "../features/preferences/types";

type Props<T> = {
  items: readonly T[];
  columns: GridColumns;
  mode: "lookbook" | "grid";
  class: string;
  busy?: boolean;
  getKey: (item: T) => string;
  children: (item: T) => JSX.Element;
};

function autoColumnCount(mode: Props<unknown>["mode"], width: number): number {
  if (mode === "lookbook") {
    if (width >= 1200) return 7;
    if (width >= 768) return 5;
    return 3;
  }
  if (width >= 1200) return 4;
  if (width >= 768) return 3;
  if (width >= 390) return 2;
  return 1;
}

export function VirtualGrid<T>(props: Props<T>) {
  let root: HTMLDivElement | undefined;
  const [width, setWidth] = createSignal(typeof window === "undefined" ? 1200 : window.innerWidth);
  const [scrollMargin, setScrollMargin] = createSignal(0);
  const columnCount = createMemo(() => props.columns === "auto"
    ? autoColumnCount(props.mode, width())
    : Number(props.columns));
  const rows = createMemo(() => {
    const result: T[][] = [];
    const columns = columnCount();
    for (let index = 0; index < props.items.length; index += columns) {
      result.push(props.items.slice(index, index + columns) as T[]);
    }
    return result;
  });
  const gap = createMemo(() => {
    if (props.mode === "lookbook") return width() >= 768 ? 12 : 8;
    if (props.columns !== "auto") return Math.max(6, 18 - Number(props.columns) * 2);
    if (width() >= 1200) return 6;
    if (width() >= 768) return 8;
    return 10;
  });
  const rowGap = createMemo(() => props.mode === "grid" ? 18 : gap());
  const estimatedRowHeight = () => {
    const itemWidth = Math.max(1, (width() - gap() * (columnCount() - 1)) / columnCount());
    // カタログの未表示行にはDOM実測値がないため、固定の加算値を
    // 入れると、その値がスクロール先でそのまま空白として現れる。
    // 画像領域の比率だけで控えめに見積もり、表示後は実測値へ置換する。
    const contentHeight = props.mode === "lookbook" ? itemWidth * 5 / 3 : itemWidth * 0.9;
    return contentHeight + rowGap();
  };
  const virtualizer = createWindowVirtualizer<HTMLDivElement>({
    get count() { return rows().length; },
    estimateSize: estimatedRowHeight,
    get scrollMargin() { return scrollMargin(); },
    getItemKey: (index) => rows()[index]?.map(props.getKey).join("\n") ?? index,
    overscan: props.mode === "grid" ? 5 : 3,
  });
  const virtualRows = () => virtualizer.getVirtualItems();
  const firstVirtualRowStart = () => virtualRows()[0]?.start ?? scrollMargin();

  onMount(() => {
    const updateGeometry = () => {
      if (!root) return;
      const nextWidth = root.clientWidth;
      const widthChanged = nextWidth !== width();
      setWidth(nextWidth);
      setScrollMargin(root.getBoundingClientRect().top + window.scrollY);
      if (widthChanged) virtualizer.measure();
    };
    const observer = new ResizeObserver(updateGeometry);
    if (root) observer.observe(root);
    window.addEventListener("resize", updateGeometry);
    updateGeometry();
    onCleanup(() => {
      observer.disconnect();
      window.removeEventListener("resize", updateGeometry);
    });
  });

  return (
    <div
      ref={root}
      aria-live="polite"
      aria-busy={props.busy}
      style={{ position: "relative", height: `${virtualizer.getTotalSize()}px`, width: "100%" }}
    >
      <div
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          transform: `translateY(${firstVirtualRowStart() - scrollMargin()}px)`,
        }}
      >
        <For each={virtualRows()}>{(virtualRow) => (
          <div
            ref={(element) => virtualizer.measureElement(element)}
            data-index={virtualRow.index}
            class={props.class}
            style={{
              width: "100%",
              "grid-template-columns": `repeat(${columnCount()}, minmax(0, 1fr))`,
              "column-gap": `${gap()}px`,
              "row-gap": "0",
              "border-bottom": `${rowGap()}px solid transparent`,
            }}
          >
            <For each={rows()[virtualRow.index]}>{(item) => props.children(item)}</For>
          </div>
        )}</For>
      </div>
    </div>
  );
}
